
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import PractitionerTable from '@/components/admin/practitioners/PractitionerTable';
import PractitionerFilters from '@/components/admin/practitioners/PractitionerFilters';
import PractitionerDetails from '@/components/admin/practitioners/PractitionerDetails';
import AdminCertificationsList from '@/components/admin/practitioners/AdminCertificationsList';
import { ThirdPartyRatingReview } from '@/components/admin/practitioners/ThirdPartyRatingReview';
import AdminPractitionerGallery from '@/components/admin/practitioners/AdminPractitionerGallery';
import { usePractitioners } from '@/hooks/use-admin-practitioners';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Shield, Images } from 'lucide-react';
import { glassCardStatic, glassTabsList } from '@/utils/admin-glass-styles';

const AdminPractitioners: React.FC = () => {
  const [selectedPractitioner, setSelectedPractitioner] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('practitioners');
  const {
    practitioners,
    loading,
    page,
    setPage,
    totalCount,
    pageSize,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    updatePractitionerStatus,
    updateVerificationStatus,
    formatDate,
  } = usePractitioners();

  // Get count of pending certifications for the badge
  const { data: pendingCount } = useQuery({
    queryKey: ['pending-certifications-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('practitioner_certifications')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending_review');

      if (error) throw error;
      return count || 0;
    },
  });

  // Get count of pending third-party rating submissions
  const { data: pendingRatingsCount } = useQuery({
    queryKey: ['pending-third-party-ratings-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('third_party_rating_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
  });

  // Get count of pending photo reviews
  const { data: pendingPhotosCount } = useQuery({
    queryKey: ['pending-photos-count'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_pending_photos_count');

      if (error) throw error;
      return data || 0;
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_review':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending Review</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getVerificationBadge = (isVerified: boolean) => {
    return isVerified 
      ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Verified</Badge>
      : <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Unverified</Badge>;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Practitioner Management</h2>
        <div className="text-sm text-gray-500">Total: {totalCount} Practitioners</div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full grid-cols-4 ${glassTabsList}`}>
          <TabsTrigger value="practitioners">Practitioners</TabsTrigger>
          <TabsTrigger value="certifications" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>Certifications</span>
            {pendingCount && pendingCount > 0 && (
              <Badge variant="destructive" className="text-xs ml-1">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="third-party-ratings" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span>Third-Party Ratings</span>
            {pendingRatingsCount && pendingRatingsCount > 0 && (
              <Badge variant="destructive" className="text-xs ml-1">
                {pendingRatingsCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="photo-gallery" className="flex items-center gap-2">
            <Images className="h-4 w-4" />
            <span>Photo Gallery</span>
            {pendingPhotosCount && pendingPhotosCount > 0 && (
              <Badge variant="destructive" className="text-xs ml-1">
                {pendingPhotosCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="practitioners">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle className="text-lg">Practitioner List</CardTitle>
            </CardHeader>
            <CardContent>
              <PractitionerFilters
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                onSearchChange={setSearchQuery}
                onStatusChange={setStatusFilter}
              />

              <PractitionerTable 
                practitioners={practitioners}
                loading={loading}
                onViewDetails={setSelectedPractitioner}
                getStatusBadge={getStatusBadge}
                getVerificationBadge={getVerificationBadge}
              />

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                          className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNumber = i + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= page - 1 && pageNumber <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={pageNumber === page}
                                onClick={() => setPage(pageNumber)}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          (pageNumber === page - 2 && pageNumber > 1) ||
                          (pageNumber === page + 2 && pageNumber < totalPages)
                        ) {
                          return <PaginationItem key={i}>...</PaginationItem>;
                        }
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                          className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}

              <AlertDialog open={!!selectedPractitioner} onOpenChange={(open) => !open && setSelectedPractitioner(null)}>
                <AlertDialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Practitioner Details</AlertDialogTitle>
                  </AlertDialogHeader>
                  <div className="py-4">
                    {selectedPractitioner && (
                      <PractitionerDetails
                        practitioner={selectedPractitioner}
                        getStatusBadge={getStatusBadge}
                        getVerificationBadge={getVerificationBadge}
                        formatDate={formatDate}
                        onUpdateStatus={updatePractitionerStatus}
                        onUpdateVerification={updateVerificationStatus}
                      />
                    )}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setSelectedPractitioner(null)}>Close</AlertDialogCancel>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle className="text-lg">Certification Review</CardTitle>
              <p className="text-sm text-gray-600">
                Review and approve/reject practitioner certifications
              </p>
            </CardHeader>
            <CardContent>
              <AdminCertificationsList />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="third-party-ratings">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle className="text-lg">Third-Party Rating Review</CardTitle>
              <p className="text-sm text-gray-600">
                Review and approve/reject third-party rating submissions from practitioners
              </p>
            </CardHeader>
            <CardContent>
              <ThirdPartyRatingReview />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="photo-gallery">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle className="text-lg">Practitioner Photo Gallery</CardTitle>
              <p className="text-sm text-gray-600">
                Review and approve/reject practitioner gallery photos
              </p>
            </CardHeader>
            <CardContent>
              <AdminPractitionerGallery />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPractitioners;
